import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ContentEditor from '../ContentEditor';
import HubImageManager from '../HubImageManager';
import IconButton from '../IconButton';
import { getImageUrl, getImageName, triggerEvent } from '../../utils';
import PlusIcon from '../../icons/green-plus.svg';

import { ContentEditorSection, EditorWrapper, ImageUploadWrapper, ImageWrapper, LoopImage, HubGuide } from './styles';

const EditorSection = ({
  editor,
  userToken,
  integrations,
  fileService,
  data,
  payload,
  setPayload,
  loading = false,
  translations,
  circle_uuid,
  type,
}) => {
  const { useAutoSave } = editor;
  const isLoop = type === 'loop';
  const imageData = isLoop ? getImageName(data.icon_image) : getImageName(data.image);

  const [description, setDescription] = useState(data.description);
  const [purpose, setPurpose] = useState(data.purpose);
  const [inventory, setInventory] = useState(data.inventory);
  const [image, setImage] = useState(imageData);
  const [currentEditor, setCurrentEditor] = useState('');
  const [imagePopupIsOpen, setImagePopupIsOpen] = useState(false);

  useEffect(() => {
    const imageUpdated = imageData !== image;
    if (data.description !== description || data.purpose !== purpose || data.inventory !== inventory || imageUpdated) {
      setPayload({
        ...payload,
        description,
        purpose,
        inventory,
        immediateSave: imageUpdated,
        [isLoop ? 'icon_image' : 'image']: image,
      });
    }
  }, [description, purpose, inventory, image]);

  useEffect(() => {
    if (data.description !== undefined && data.description !== description) {
      setDescription(data.description);
    }
  }, [data.description]);

  useEffect(() => {
    if (data.purpose !== undefined && data.purpose !== purpose) {
      setPurpose(data.purpose);
    }
  }, [data.purpose]);

  useEffect(() => {
    if (data.inventory !== undefined && data.inventory !== inventory) {
      setInventory(data.inventory);
    }
  }, [data.inventory]);

  useEffect(() => {
    if (imageData !== image) {
      setImage(imageData);
    }
  }, [imageData]);

  useEffect(() => {
    if (!currentEditor) {
      setCurrentEditor('description');
    }
  }, []);

  const onChangeDescription = useCallback((value) => {
    setDescription(value);
  }, []);

  const onChangePurpose = useCallback((value) => {
    setPurpose(value);
  }, []);

  const onChangeInventory = useCallback((value) => {
    setInventory(value);
  }, []);

  const imageSelected = (fileName) => {
    setImage(fileName);
    setImagePopupIsOpen(false);
    if (useAutoSave) {
      triggerEvent(`${type}.updated`, {
        uuid: data.uuid,
        name: data.name,
        fileName: getImageUrl(fileService, fileName, isLoop ? '100x100' : '220x220'),
      });
    }
  };

  const placeholders = isLoop ? translations.circle.placeholder : translations.editor.placeholder;

  const getEditor = (editorType, placeholder, value, onChange) => (
    <ContentEditor
      id={`contentEditor_${editorType}`}
      type={type}
      aria-label={placeholder}
      placeholder={placeholder}
      value={value}
      onChange={(value) => !loading && onChange(value)}
      loading={loading}
      onFocus={() => setCurrentEditor(editorType)}
      autoFocus={editorType === 'description'}
      integrations={integrations}
      fileService={fileService}
      canToggleToolbar={false}
      showToolbar={currentEditor === editorType}
      darkMode={true}
      memoize={true}
    />
  );

  return (
    <ContentEditorSection>
      <EditorWrapper>
        {getEditor(
          'description',
          isLoop ? placeholders.extended_description : placeholders.hub_body,
          description,
          onChangeDescription,
        )}
      </EditorWrapper>
      <ImageUploadWrapper>
        <IconButton icon={PlusIcon} disabled={loading} onClick={() => setImagePopupIsOpen(true)}>
          {i18n.__('selectImage')}
        </IconButton>
        <ImageWrapper>
          {image ? (
            <LoopImage
              src={getImageUrl(fileService, image, '100x100')}
              disabled={loading}
              onClick={() => setImagePopupIsOpen(true)}
            />
          ) : null}
        </ImageWrapper>
      </ImageUploadWrapper>
      <EditorWrapper isTeacherText={true}>
        {isLoop ? (
          getEditor('purpose', placeholders.inventory, purpose, onChangePurpose)
        ) : (
          <>
            <HubGuide>{translations.editor.hub.guide}</HubGuide>
            {getEditor('inventory', placeholders.hub_guide_body, inventory, onChangeInventory)}
          </>
        )}
      </EditorWrapper>
      <HubImageManager
        loopId={circle_uuid}
        currentImage={image}
        userToken={userToken}
        fileService={fileService}
        popupIsOpen={imagePopupIsOpen}
        onClose={() => setImagePopupIsOpen(false)}
        onSave={imageSelected}
        onRender={() => {}}
      />
    </ContentEditorSection>
  );
};

export default EditorSection;

EditorSection.propTypes = {
  editor: PropTypes.object.isRequired,
  userToken: PropTypes.string.isRequired,
  integrations: PropTypes.object.isRequired,
  fileService: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  payload: PropTypes.object.isRequired,
  setPayload: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  translations: PropTypes.object.isRequired,
  circle_uuid: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};
